/// <reference types="googlepay" />
import {
    TButtonData,
    TButtonDataMessages,
    TCommonData,
    TCommonDataObj,
    TCommonInputMessages,
    TCommonMessages,
    TInputData,
    TInputPhoneData,
    TItem,
    TTextData,
    TWidget,
    TWidgetCommonOption,
} from '@libs/types';
import {
    ECardBalancePaymentSystem,
    ECardBalanceStatus,
    ECardView,
    GooglePayTokenizationType,
    RowEnum,
    StatusTrackerEnum,
    TransactionStatusEnum,
} from './enums';

type TLimitBarValue = {
    currentAmount: number;
    maxAmount: number;
    currencyIso: string;
} & TCommonDataObj;

type TLimitBarMessages = {
    limitTitle?: string;
    currentSubtitle?: string;
    maxSubtitle?: string;
} & TCommonMessages;

export type TLimitBarData = {
    value: TLimitBarValue;
    messages?: TLimitBarMessages;
};

export type TBar = TLimitBarData;

type TReferralsProgressValue = {
    earnedAmount: number;
    totalAmount: number;
    totalSteps: number;
} & TCommonDataObj;

type TReferralsProgressMessages = {
    referralTitle?: string;
    startSubtitle?: string;
    progressSubtitle?: string;
} & TCommonMessages;

export type TReferralsProgressData = {
    value: TReferralsProgressValue;
    messages?: TReferralsProgressMessages;
} & TCommonData;

export type TConsumerItem = {
    value: string | TCommonDataObj | TContactValue;
    text?: string;
    subtitle?: string;
    countryName?: string;
    additionalSubtitle?: string;
    date?: string | Date;
    amount?: string;
    currency?: string;
    direction?: 'TRANSFER' | 'WITHDRAW' | 'INCOME' | 'EXCHANGE';
    countryIso?: string;
    iconName?: string;
    defaultIconName?: string;
    avatar?: string;
    alias?: Array<string>;
    flagName?: string;
    iconVariant?: 'simple' | 'circle';
    iconHierarchy?: 'emphasised' | 'primary' | 'secondary' | 'secondaryAlt' | 'destructive';
    background?: string;
    imageSource?: string;
    style?: string;
    backgroundUrl?: string;
    flagIconSmall?: string;
    status?: TransactionStatusEnum;
    statusText?: string;
    metaText?: string;
    metaVariant?: 'text' | 'tag';
    metaHierarchy?: 'primary' | 'secondary' | 'inverted';
    clickable?: boolean;
    from?: boolean;
    to?: boolean;
    iso?: string;
    contentPosition?: string;
    buttonText?: string;
    buttonIconName?: string;
    buttonVisible?: boolean;
    images?: TStoriesImages;
    fullwidthImage?: boolean;
    enabled?: boolean;
} & TItem &
    Pick<TWidgetCommonOption, 'isDisabled'>;

export type TStoriesImages = {
    bgImageA?: string;
    bgImageA2x?: string;
    bgImageA3x?: string;
    bgImageB?: string;
    bgImageB2x?: string;
    bgImageB3x?: string;
};

export type TContactValue = {
    email?: string;
    avatar?: string;
    fullName?: string;
    number?: string;
};

export type TBalance = {
    id: string;
    balance: number;
    name: string;
    iso: string;
};

type TSection = {
    header: string;
    itemsIds: Array<string>;
};

export type TCarouselData = {
    items: Array<TConsumerItem>;
} & TCommonData;

type TDropdownMessages = {
    selectItemTitle?: string;
} & TButtonDataMessages;

type TDropdownItems = {
    source?: string;
} & TItem;
export type TDropdownData = {
    items?: Array<TDropdownItems>;
    messages?: TDropdownMessages;
} & TInputData;

type TInputCommonMessages = {
    helperText?: string;
} & TCommonMessages;

export type TInputPhoneMessages = {
    label: string;
    allItemsHeader: string;
    searchPlaceholder: string;
    emptySearchResultLabel: string;
    noSuggestionsError: string;
    headerForSelectPhonePrefix: string;
    errorMessageDescription?: string;
    buttonFirstLabelLabel?: string;
    buttonSecondLabelLabel?: string;
} & TInputCommonMessages;

type TTInputSearchAddressMessages = {
    searchPlaceholder: string;
    noSuggestionsError: string;
    continueButtonTitle: string;
} & TInputPhoneMessages;
export type TListDataMessages = {
    emptyPlaceholder: string;
    today: string;
    yesterday: string;
    awaiting: string;
    monthList: Array<string>;
} & TInputPhoneMessages;

export type TConsumerListData = {
    allItemsHeader?: string;
    emptyPlaceholder?: string;
    emptySearchResultLabel?: string;
    searchPlaceholder?: string;
    items: Array<TConsumerItem>;
    sections?: TSection[];
    templates?: Array<TWidget<TCommonData>>;
    cellWidgetId?: RowEnum.SELECTOR_REGULAR | RowEnum.ROW_REGULAR;
    messages?: TListDataMessages;
    settings?: TListSettings;
} & TCommonData &
    TButtonData &
    TRowRegularData;

type TRowRegularData = {
    title?: string;
    subtitle?: string;
    metaText?: string;
    iconName?: string;
    flagIcon?: string;
    avatar?: string;
} & TButtonData;

type TBalanceMessages = {
    selectBalanceTitle: string;
    addBalanceButtonTitle: string;
    exchangeButtonTitle: string;
    getCardButtonTitle: string;
} & TCommonMessages;
export type TBalanceData = {
    balances: Array<TBalance>;
    messages: TBalanceMessages;
    canAddCurrency: boolean;
    canExchange: boolean;
    canGetPaysendCard: boolean;
} & TCommonData;
type TDetailsSeparateMessages = {
    copiedText?: string;
};
export type TDetailsSeparateTextData = {
    leftText?: string;
    rightText?: string;
    messages?: TDetailsSeparateMessages;
} & TCommonData;
/**
 * @property {string} text - This text will find in data.text and transform based on parameters
 * @property {string} link - String will be represented as hyperlink
 * @property {string} textColor - Color for text
 * @property {string} actionName - This field makes part of the text clickable and indicates which action.name should be used for sending the action
 * @property {boolean} isRepeatable - Use this transformation to the all parts which can be detected. Not only to the first one.
 * @property {boolean} smallCaps - For this text will apply roles for smallСaps text
 * @property {boolean} useLocaleNumberFormat - The value from the text property will be converted to the user format according to its regional settings.
 * The text value must contain only numbers without spaces or other symbols.
 * @property {number} precision - Shows what precision will be displayed in the fractional part
 */
export type TTextTransformation = {
    text?: string;
    link?: string;
    textColor?: string;
    actionName?: string;
    smallCaps?: boolean;
    isRepeatable?: boolean;
    useLocaleNumberFormat?: boolean;
    precision?: number;
};
export type TTextConsumerData = {
    textTransformation?: Array<TTextTransformation>;
} & TTextData;

export type TSeparatorData = {
    text?: string;
    metaText?: string;
} & TCommonData;

type TPromoStoriesMessages = {
    topLeftButtonLabel?: string;
    topRightButtonLabel?: string;
    bottomLeftButtonLabel?: string;
    bottomRightButtonLabel?: string;
};
export type TPromoStoriesData = {
    slides?: Array<TConsumerItem>;
    messages?: TPromoStoriesMessages;
    topLeftButtonIconName?: string;
    topLeftButtonFlagIcon?: string;
    topRightButtonIconName?: string;
    topRightButtonFlagIcon?: string;
    duration?: number;
} & TCommonData;

type TListSettings = {
    paging?: TListPaging;
};
type TListPaging = {
    size?: number;
    last?: string;
    theEnd?: boolean;
};

export type TVisualData = {
    iconName?: string;
    defaultIconName?: string;
    flagIcon?: string;
    flagIconSmall?: string;
    avatar?: string;
    base64?: string;
} & TCommonData;

export type TConsumerInputPhoneItem = {
    maxLength?: number;
} & TItem;
export type TConsumerInputPhoneData = {
    messages?: TInputPhoneMessages;
    buttonFirstLabelIcon?: string;
    buttonSecondLabelIcon?: string;
    items: Array<TConsumerInputPhoneItem>;
} & TInputPhoneData &
    TConsumerListData;

export type TInputSearchAddressItem = {
    details: TCommonDataObj;
} & TConsumerItem;

export type TInputSearchAddress = {
    items: Array<TInputSearchAddressItem>;
    messages: TTInputSearchAddressMessages;
    minQueryLength?: number;
} & TConsumerInputPhoneData;

export type TMpiData = {
    redirect: string;
    collectBrowserDataUri: string;
} & TCommonData;

export type TKYC = TSumsub & TZendeskChat;

export type TSumsub = {
    accessToken: string;
} & TCommonData;

export enum KycProviderName {
    SUMSUB = 'sumsub',
    PERSONA = 'persona',
}

export type TKycSumsubCredentials = {
    accessToken: string;
    locale?: string;
};

export type TKycPersonaCredentials = {
    /** Mode 1 — resume/launch an inquiry created by the backend. */
    inquiryId?: string;
    sessionToken?: string;
    /** Mode 2 — create a new inquiry from a template on launch. */
    templateId?: string;
    environmentId?: string;
    environment?: 'production' | 'sandbox';
    referenceId?: string;
    firstName?: string;
    lastName?: string;
    /** Common */
    locale?: string;
};

export type TKycProviderCredentials = TKycSumsubCredentials | TKycPersonaCredentials;

export type TKycProviderValue = {
    provider: KycProviderName;
    credentials: TKycProviderCredentials;
};

export type TKycProviderData = {
    value?: TKycProviderValue;
    text?: string;
} & TCommonData;

export enum KycOutcome {
    SUCCESS = 'success',
    ERROR = 'error',
    CANCEL = 'cancel',
}

export enum KycErrorType {
    INVALID_DATA = 'invalidData',
    PROVIDER_ERROR = 'providerError',
    VERIFICATION_DECLINED = 'verificationDeclined',
    VERIFICATION_REJECTED = 'verificationRejected',
}

export type TKycProviderOutput = {
    provider: KycProviderName | null;
    outcome: KycOutcome;
    errorType: KycErrorType | null;
    providerStatus: string | null;
    inquiryId: string | null;
};

export type TZendeskChat = {
    zendeskKey?: string;
    zendeskToken: string;
    conversationId?: string;
    extras: Record<string, string>;
} & TButtonData;

type TRowButtonDataMessages = {
    title?: string;
    subtitle?: string;
    meta?: string;
} & TCommonMessages;
type TRowActionMessages = {
    title: string;
    subtitle: string;
    helperText: string;
    copiedText: string;
} & TCommonMessages;

export type TRowActionData = {
    messages?: TRowActionMessages;
} & TRowButton;
export type TButtonRowEmphasised = {
    expired?: boolean;
} & TRowButton;

export type TButtonBannerData = {
    hasNotification?: boolean;
} & TButtonData;

type TRowButton = {
    iconName?: string;
    avatar?: string;
    messages: TRowButtonDataMessages;
} & TButtonData;

type TMerchantInfo = {
    merchantName?: string;
};

export type TGooglePay = {
    value: TGooglePayValue;
    protocolVersion: string;
    supportedAuthMethods: google.payments.api.CardAuthMethod[];
    environment?: 'ENVIRONMENT_TEST' | 'ENVIRONMENT_PRODUCTION';
    publicKey: string;
    allowedCountryCodes: Array<string>;
    supportedNetworks: google.payments.api.CardNetwork[];
    countryCode: string;
    merchantId: string;
    gatewayMerchantId: string;
    messages: TGooglePayMessages;
    tokenizationType: GooglePayTokenizationType;
    gateway: string;
    merchantInfo?: TMerchantInfo;
} & TCommonData;

export type TApplePay = {
    authorizeMerchant: unknown;
} & TGooglePay;

export type TSignInWithAppleData = {
    requestedScopes?: string[];
    nonce?: string;
    messages?: Pick<TButtonDataMessages, 'label'>;
} & TCommonData;

export type TSignInWithGoogleData = {
    nonce?: string;
    messages?: Pick<TButtonDataMessages, 'label'>;
} & TCommonData;

type TGooglePayValue = {
    amount: number;
    curIso: string;
};

type TGooglePayMessages = {
    paymentLabel?: string;
} & TCommonMessages;
export type TCardBalanceData = {
    maskedPan?: string;
    pan?: string;
    cardholderName?: string;
    expirationDate?: string;
    cvv?: string;
    appleWalletAdded?: boolean;
    googleWalletAdded?: boolean;

    suspiciousActivity?: boolean;
    expired?: boolean;
    frozen?: boolean;
    isCardIssued?: boolean;
    isExpanded?: boolean;

    paymentSystem?: ECardBalancePaymentSystem;
    status?: ECardBalanceStatus;
    zendeskChat?: TZendeskChat;
    messages?: TCardBalanceDataMessages;
} & TCommonData;

export type TCardBalanceDataMessages = {
    done: string;
    cardDetailsBtn: string;
    freezeCardBtn: string;
    unfreezeCardBtn: string;
    reissueBtn: string;
    reissueCardBtn: string;
    supportBtn: string;
    moreBtn: string;
    getNewCardBtn: string;
    hideDetailsBtn: string;
    contactSupportBtn: string;
    cancelReissueCardBtn: string;
    cardDescription: string;
    cardNumberTitle: string;
    expirationDateTitle: string;
    cardholderNameTitle: string;
    cvvTitle: string;
    copiedText: string;
    reissueDescription: string;
    metaText: string;
    getCard: string;
} & TCommonMessages;

export type TCardView = {
    pan: string;
    expirationDate: string;
    paymentSystem: string;
    bankTitle: string;
    bankImage: string;
    accountNumber: number;
    status: ECardView;
    messages: TCardViewMessages;
} & TCommonData;

type TCardViewMessages = {
    accountNumberPlaceholder: string;
    expDate: string;
} & TCommonMessages;

export type TValidationState = {
    validationState: 'isFuture' | 'isBefore' | 'isAbove';
    years?: number;
    months?: number;
    days?: number;
    errorMessage?: string;
};

export type TDateInputData = {
    helperText?: string;
    validationsStates?: Array<TValidationState>;
} & TInputData;

export type TWebViewLayout = {
    value?: string;
    redirects?: Array<TRedirectObject>;
};

type TRedirectObject = {
    url: string;
    actionName: string;
};

export type TBrazeBanner = {
    userId: string;
    placementId: string;
} & TCommonData;

type TStatusTrackerItems = {
    title: string;
    subtitle: string;
    descriptionLines: Array<string>;
    state: StatusTrackerEnum;
};

export type TStatusTrackerData = {
    items?: [TStatusTrackerItems];
} & TCommonData;

type TMenuButtonMessages = Pick<TButtonDataMessages, 'label' | 'errorMessage'> & Pick<TConsumerItem, 'metaText'>;

export type TMenuButtonData = {
    messages?: TMenuButtonMessages;
} & TCommonData;

export type TSelectorRegularData = {
    value: boolean;
    title: string;
    subtitle: string;
    metaText: string;
    topText?: string;
    subtitleIconName?: string;
    messages: { topText?: string } & Pick<TRowButtonDataMessages, 'title' | 'subtitle'> & Pick<TConsumerItem, 'metaText'>;
} & TRowButton;

type TInputPasscodeMessages = {
    useBothBiometryTitle?: string;
} & TCommonInputMessages;

export type TInputPasscodeData = {
    messages: TInputPasscodeMessages;
} & TInputData;

type TInfoBannerMessages = {
    title?: string;
    subtitle?: string;
};

export type TInfoBannerData = {
    messages?: TInfoBannerMessages;
} & TCommonData;
